import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';

export interface CalendarDayCell {
  day: number;
  image: string;
  amount: number;
  item_name: string;
  status: number;
  is_today: boolean;
  is_consecutive: number;
  CodeName128: string;
  item_quantity: number;
}

export interface CalendarBonusReward {
  day: number;
  image: string;
  amount: number;
  item_name: string;
  CodeName128: string;
  item_quantity: number;
  status: number;
}

export interface CalendarState {
  campaign: {
    id: number;
    campaign_id: string;
    name: string;
    description?: string | null;
    start_date: string;
    end_date: string;
  } | null;
  campaignId: string;
  calendarData: Record<string | number, CalendarDayCell>;
  currentEvent: string;
  description: string;
  startDate: string;
  endDate: string;
  daysChecked: number;
  totalDays: number;
  daysMissed: number;
  daysRemaining: number | string;
  consecutiveDays: number;
  canCheckinToday: boolean;
  lastClaimedAt: string | null;
  dbNow: string;
  todayDayNumber: number;
  bonusReward: CalendarBonusReward | null;
  bonusRewards?: CalendarBonusReward[];
}

export const CALENDAR_QUERY_KEY = ['calendar'] as const;

export async function fetchCalendarState(): Promise<CalendarState> {
  const { data } = await api.get<CalendarState & { status: string }>('/api/v1/calendar');
  return data;
}

export function useCalendar() {
  return useQuery({
    queryKey: CALENDAR_QUERY_KEY,
    queryFn: fetchCalendarState,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useCalendarMutations() {
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: CALENDAR_QUERY_KEY });

  /** One-click check-in + claim (preferred). */
  const claimDay = useMutation({
    mutationFn: async (payload: {
      campaign_id: string;
      day_number: number;
      CodeName128?: string;
      item_quantity?: number;
    }) => {
      const { data } = await api.post<{ success: boolean; message: string }>(
        '/api/v1/calendar/claim-day',
        payload,
      );
      return data;
    },
    onSuccess: (data) => {
      if (data.success) {
        window.setTimeout(() => invalidate(), 750);
      } else {
        invalidate();
      }
    },
  });

  /** Legacy: check-in only. Kept for compatibility. */
  const checkin = useMutation({
    mutationFn: async (payload: { campaign_id: string; day_number: number }) => {
      const { data } = await api.post<{ success: boolean; message: string }>(
        '/api/v1/calendar/checkin',
        payload,
      );
      return data;
    },
    onSuccess: () => {
      window.setTimeout(() => invalidate(), 750);
    },
  });

  /** Legacy: claim item only (status must be 1). */
  const claimReward = useMutation({
    mutationFn: async (payload: {
      campaign_id: string;
      day_number: number;
      CodeName128: string;
      item_quantity: number;
    }) => {
      const { data } = await api.post<{ success: boolean; message: string }>(
        '/api/v1/calendar/claim-reward',
        payload,
      );
      return data;
    },
    onSuccess: (data) => {
      if (data.success) {
        window.setTimeout(() => invalidate(), 750);
      } else {
        invalidate();
      }
    },
  });

  return { claimDay, checkin, claimReward };
}

export function prefetchCalendar(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.prefetchQuery({
    queryKey: CALENDAR_QUERY_KEY,
    queryFn: fetchCalendarState,
    staleTime: 30_000,
  });
}
